import { AppModuleCode, PermissionCode, RoleCode, type AuthUser } from '@gb-ticket/shared-types';

import { hasPermission, isModuleEnabled } from '@/platform/auth/permissions';

export const ADMIN_ROOT = '/verwaltung';
export const USERS_ADMIN_PATH = `${ADMIN_ROOT}/benutzer`;
/** Who may publish intranet posts in another person's name. */
export const AUTHOR_GRANTS_ADMIN_PATH = `${USERS_ADMIN_PATH}/beitraege-fuer-andere`;
export const DEPARTMENTS_ADMIN_PATH = `${ADMIN_ROOT}/abteilungen`;
export const SITES_ADMIN_PATH = `${ADMIN_ROOT}/standorte`;
export const TEAMS_ADMIN_PATH = `${ADMIN_ROOT}/teams`;
export const ROLES_ADMIN_PATH = `${ADMIN_ROOT}/berechtigungsgruppen`;
/** Modul-Store (E5). Legacy bookmarks under `/verwaltung/produkte` redirect here. */
export const MODULES_ADMIN_PATH = `${ADMIN_ROOT}/module`;
export const PRODUCTS_ADMIN_PATH = MODULES_ADMIN_PATH;
export const PRODUCTS_ADMIN_LEGACY_PATH = `${ADMIN_ROOT}/produkte`;
export const AI_ADMIN_PATH = `${ADMIN_ROOT}/ki`;
export const MAIL_ADMIN_PATH = `${ADMIN_ROOT}/e-mail`;
export const DIRECTORY_ADMIN_PATH = `${ADMIN_ROOT}/verzeichnis`;
export const AUDIT_ADMIN_PATH = `${ADMIN_ROOT}/audit`;
export const TENANTS_ADMIN_PATH = `${ADMIN_ROOT}/mandanten`;
export const VOICE_ADMIN_PATH = `${ADMIN_ROOT}/anrede`;
export const TYPOGRAPHY_ADMIN_PATH = `${ADMIN_ROOT}/darstellung`;
export const NOTIFICATION_STANDARD_ADMIN_PATH = `${ADMIN_ROOT}/mitteilungs-standard`;

export function userAdminPath(userId: string): string {
  return `${USERS_ADMIN_PATH}/${userId}`;
}

export type CoreAdminSectionId =
  | 'users'
  | 'departments'
  | 'sites'
  | 'teams'
  | 'roles'
  | 'products'
  | 'ai'
  | 'mail'
  | 'directory'
  | 'audit'
  | 'tenants'
  | 'voice'
  | 'typography'
  | 'notificationStandard';

/** Core Verwaltung section id. */
export type AdminSectionId = string;

export type PlatformAdminLink = {
  id: AdminSectionId;
  to: string;
  label: string;
  description: string;
};

type CoreAdminSection = PlatformAdminLink & {
  id: CoreAdminSectionId;
  visible: (user: AuthUser) => boolean;
};

const canReadUsers = (user: AuthUser) => hasPermission(user, PermissionCode.USERS_READ);

const CORE_ADMIN_SECTIONS: readonly CoreAdminSection[] = [
  {
    id: 'users',
    to: USERS_ADMIN_PATH,
    label: 'Benutzer',
    description: 'Konten, Rollen und Zuordnungen',
    visible: canReadUsers,
  },
  {
    id: 'departments',
    to: DEPARTMENTS_ADMIN_PATH,
    label: 'Abteilungen',
    description: 'Organisationsstruktur',
    visible: canReadUsers,
  },
  {
    id: 'sites',
    to: SITES_ADMIN_PATH,
    label: 'Standorte',
    description: 'Büros und Einsatzorte',
    visible: canReadUsers,
  },
  {
    id: 'teams',
    to: TEAMS_ADMIN_PATH,
    label: 'Teams',
    description: 'Teams, Leitung und Mitglieder',
    visible: canReadUsers,
  },
  {
    id: 'roles',
    to: ROLES_ADMIN_PATH,
    label: 'Berechtigungsgruppen',
    description: 'Rollen, Rechte und Standardzuordnungen',
    visible: (user) => hasPermission(user, PermissionCode.USERS_ASSIGN_ROLE),
  },
  {
    id: 'products',
    to: MODULES_ADMIN_PATH,
    label: 'Module',
    description: 'Module dieses Mandanten',
    visible: (user) => hasPermission(user, PermissionCode.PRODUCTS_MANAGE),
  },
  {
    id: 'ai',
    to: AI_ADMIN_PATH,
    label: 'KI',
    description: 'Anschluss an Ollama, OpenAI oder Grok und den Intranet-Assistenten.',
    visible: (user) => hasPermission(user, PermissionCode.AI_MANAGE),
  },
  {
    id: 'mail',
    to: MAIL_ADMIN_PATH,
    label: 'E-Mail',
    description: 'Versand und Postfach des Portals',
    visible: (user) => hasPermission(user, PermissionCode.MAIL_MANAGE),
  },
  {
    id: 'directory',
    to: DIRECTORY_ADMIN_PATH,
    label: 'Verzeichnis',
    description: 'Abgleich mit Microsoft 365 und LDAP',
    visible: (user) => hasPermission(user, PermissionCode.DIRECTORY_MANAGE),
  },
  {
    id: 'audit',
    to: AUDIT_ADMIN_PATH,
    label: 'Audit-Log',
    description: 'Wer hat wann was geändert',
    visible: (user) =>
      isModuleEnabled(user, AppModuleCode.AUDIT) && hasPermission(user, PermissionCode.AUDIT_READ),
  },
  {
    id: 'tenants',
    to: TENANTS_ADMIN_PATH,
    label: 'Mandanten',
    description: 'Firmen dieser Installation anlegen und sperren',
    visible: (user) => hasPermission(user, PermissionCode.TENANTS_MANAGE),
  },
  {
    id: 'voice',
    to: VOICE_ADMIN_PATH,
    label: 'Anrede',
    description: 'platform.admin.voice.description',
    visible: (user) => hasPermission(user, PermissionCode.VOICE_MANAGE),
  },
  {
    id: 'typography',
    to: TYPOGRAPHY_ADMIN_PATH,
    label: 'Darstellung',
    description:
      'Standard für Erscheinungsbild, Schrift und Schriftgröße. Gilt für alle, die selbst nichts gewählt haben.',
    visible: (user) => hasPermission(user, PermissionCode.TYPOGRAPHY_MANAGE),
  },
  {
    id: 'notificationStandard',
    to: NOTIFICATION_STANDARD_ADMIN_PATH,
    label: 'Mitteilungs-Standard',
    description:
      'Vorgabe für Glocke, E-Mail und Push. Gilt für alle, die selbst nichts gespeichert haben.',
    visible: (user) => user.roles?.includes(RoleCode.IT_ADMIN) === true,
  },
];

export type CoreAdminLinkGroupId = 'organisation' | 'zugang' | 'betrieb';

/** Core group id or a ProductCode for an installed-module group. */
export type AdminLinkGroupId = string;

export type AdminLinkGroup = {
  id: AdminLinkGroupId;
  heading: string;
  links: PlatformAdminLink[];
};

const CORE_ADMIN_GROUP_DEFS: readonly {
  id: CoreAdminLinkGroupId;
  heading: string;
  sectionIds: CoreAdminSectionId[];
}[] = [
  {
    id: 'organisation',
    heading: 'Organisation',
    sectionIds: ['users', 'departments', 'sites', 'teams'],
  },
  {
    id: 'zugang',
    heading: 'Zugang',
    sectionIds: ['roles', 'directory'],
  },
  {
    id: 'betrieb',
    heading: 'Betrieb',
    sectionIds: [
      'products',
      'ai',
      'audit',
      'mail',
      'voice',
      'typography',
      'notificationStandard',
      'tenants',
    ],
  },
];

function coreAdminLinks(user: AuthUser): PlatformAdminLink[] {
  return CORE_ADMIN_SECTIONS.filter((section) => section.visible(user)).map(
    ({ id, to, label, description }) => ({ id, to, label, description }),
  );
}

/**
 * Central administration sections the user may open, in sub-navigation order.
 * Product settings stay in the module (PRODUCT_META.admin), not here.
 */
export function platformAdminLinks(user: AuthUser): PlatformAdminLink[] {
  return platformAdminLinkGroups(user).flatMap((group) => group.links);
}

/** Same links as platformAdminLinks, grouped for the Verwaltung overview. */
export function platformAdminLinkGroups(user: AuthUser): AdminLinkGroup[] {
  const byId = new Map(coreAdminLinks(user).map((link) => [link.id, link]));
  return CORE_ADMIN_GROUP_DEFS.map((group) => ({
    id: group.id,
    heading: group.heading,
    links: group.sectionIds.flatMap((id) => {
      const link = byId.get(id);
      return link ? [link] : [];
    }),
  })).filter((group) => group.links.length > 0);
}

export function canOpenAdmin(user: AuthUser): boolean {
  return platformAdminLinks(user).length > 0;
}

/** Old top-level admin paths → `/verwaltung/...` (`null` when the path is not an old admin path). */
export function legacyAdminTarget(pathname: string): string | null {
  const match = /^\/(benutzer|abteilungen|teams|berechtigungsgruppen|audit)(\/.*)?$/.exec(pathname);
  if (!match) {
    return null;
  }
  return `${ADMIN_ROOT}/${match[1]}${match[2] ?? ''}`;
}
